import { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useMedia } from 'hooks/useMedia'
import { ProductCard } from '~/modules/ProductCard/ProductCard'
import { useCioSearchResults } from '../hooks/useCioSearchResults'
import { useRouter } from 'next/router'
import { LoadingColumns } from '../../PLP/components/LoadingColumns'
import { ButtonMain } from '~/elements/Button/ButtonMain'
import { GridToggle } from '../../PLP/components/GridToggle'
import { CioFilters } from './CioFilters'
import { mapProduct } from '../lib/mapProduct'
import { mapFilters } from '../lib/mapFilters'
import { CioSortSelect } from './CioSortSelect'
import { FilterTrigger } from '../../PLP/components/FilterTrigger'
import { Result, SortOption } from '@constructor-io/constructorio-client-javascript'
import { parseFilterString } from '../../PLP/lib/parseFilterString'
import { Aggregation } from '@magentoTypes'
import { useScrollLock } from '~/lib/scrollLock'
import { ParsedUrlQuery } from 'querystring'
import { hasSpecialPrice } from '~/lib/cioHelpers'
import { VariationsStrategy } from '../lib/constants'
import { PAGE_SIZE } from '~/config/constants'

type CioResultListProps = {
  results: ReturnType<typeof useCioSearchResults>
  showVariationsStrategy?: VariationsStrategy
}

type GridValues = 1 | 2 | 3 | 4 | 6

export const CioResultList = ({ results, showVariationsStrategy }: CioResultListProps) => {
  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    isFetchingNextPage,
    isFetchingPreviousPage,
    params,
  } = results
  const isDesktop = useMedia('lg')
  const { lockScroll, unlockScroll } = useScrollLock()
  const { t } = useTranslation()
  const router = useRouter()
  const [gridColumnsNumber, setGridColumnsNumber] = useState<GridValues>(isDesktop ? 6 : 2)
  const [showFilters, setShowFilters] = useState(false)
  const filters = mapFilters(results.data?.pages[0]?.response?.facets)
  const hasResults = data?.pages?.[0]?.response?.results?.length > 0
  const sortingOptions = data?.pages[0]?.response?.sort_options
  const [initialOpen, setInitialOpen] = useState<number>()
  const { type: cioType, subject: cioSubject } = params

  const currentPage = Number(router.query.page) || 1
  const totalCount = data?.pages?.[0]?.response?.total_num_results
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  useEffect(() => {
    if (data?.pages?.[0]?.response?.results) {
      setIsInitialLoading(false)
    }
  }, [data])

  useEffect(() => {
    if (showFilters) {
      lockScroll()
    } else {
      unlockScroll()
    }
  }, [showFilters, lockScroll, unlockScroll])

  const mutateQuery = useCallback(
    async (query: ParsedUrlQuery) => {
      await router?.push({ pathname: router.pathname, query }, undefined, {
        shallow: true,
        scroll: false,
      })
    },
    [router],
  )

  useEffect(() => {
    const addPageOne = async () => {
      await mutateQuery({
        ...router.query,
        page: '1',
      })
    }
    if (!router?.query?.page && router.isReady) {
      addPageOne()
    }
  }, [router, mutateQuery])

  const loadMore = async () => {
    await mutateQuery({
      ...router.query,
      page: `${Number(router.query.page) + 1}`,
    })
    await fetchNextPage()
  }

  useEffect(() => {
    isDesktop ? setGridColumnsNumber(6) : setGridColumnsNumber(2)
  }, [isDesktop])

  const loadLess = async () => {
    await mutateQuery({
      ...router.query,
      page: `${Number(router.query.page) - 1}`,
    })
    await fetchPreviousPage()
  }

  const prevPageParam = () => {
    if (Number(data?.pageParams?.[0] ?? 1) > 1) {
      return Number(data?.pageParams?.[0]) - 1
    }
    if (Number(router.query?.page) > 1) {
      return Number(router.query?.page) - 1
    }
    return undefined
  }

  const handleFilterChange = useCallback(
    async (filter: string) => {
      try {
        await mutateQuery({
          ...router.query,
          page: '1',
          filter,
        })
        // Close the dialogs
        setShowFilters(false)
      } catch (e) {
        console.error(e)
      }
    },
    [router, mutateQuery],
  )

  const selectedFilters = useMemo((): (Aggregation & { amount?: number })[] => {
    const activeFilters = parseFilterString(router.query.filter as string)
    const usedFilters = filters
      ?.filter((filter) => {
        return Object.keys(activeFilters).includes(filter.attribute_code)
      })
      .map((filters) => ({
        ...filters,
        amount: activeFilters[filters.attribute_code].length,
      }))

    return usedFilters?.length > 0 ? usedFilters.slice(0, 3) : []
  }, [filters, router.query.filter])

  const handleSortChange = useCallback(
    async (option: SortOption) => {
      await mutateQuery({
        ...router.query,
        sort: option.sort_by,
        order: option.sort_order,
      })
    },
    [router, mutateQuery],
  )

  const handleOpenExactFilter = useCallback(
    (attributeCode?: string) => {
      if (!attributeCode) {
        setInitialOpen(undefined)
      } else {
        filters.map((filter, index) => {
          if (filter.attribute_code === attributeCode) {
            setInitialOpen(index)
          }
        })
      }
    },
    [filters],
  )

  if (data?.pages[0]?.response?.redirect?.data) {
    window.location.href = data?.pages[0]?.response?.redirect?.data?.url
    return null
  }

  const hasSizeFilter = selectedFilters.some((filter) => filter.attribute_code === 'size')

  const productMapper = (result: Result) => {
    if (showVariationsStrategy === 'sale') {
      if (hasSpecialPrice(result?.data?.special_price)) {
        return {
          product: mapProduct(result, true),
          hidePriceFromSuffix: true,
          urlSelectedSize: result.data?.size_id,
        }
      }

      const selectedVariationIndex = result.variations?.findIndex((variation) =>
        hasSpecialPrice(variation?.data?.special_price),
      )
      if (selectedVariationIndex > -1) {
        return {
          product: mapProduct(result, true, selectedVariationIndex),
          hidePriceFromSuffix: true,
          urlSelectedSize: result.data?.size_id,
        }
      }
    }

    return {
      product: mapProduct(result, !!showVariationsStrategy || hasSizeFilter),
      hidePriceFromSuffix: !!showVariationsStrategy || hasSizeFilter,
      urlSelectedSize: !!showVariationsStrategy || hasSizeFilter ? result.data?.size_id : undefined,
    }
  }

  const getImageLoadStrategy = (index: number) => {
    if (isDesktop) {
      return index <= 5 ? 'eager' : 'lazy'
    }
    return index <= 1 ? 'eager' : 'lazy'
  }

  return (
    <>
      <div
        className={clsx(
          'flex items-center justify-between py-6 px-5 lg:py-8 lg:px-[140px] lgx:px-[140px]',
        )}
      >
        <div className="flex cursor-pointer items-center gap-2.5">
          {filters && filters.length > 0 && (
            <FilterTrigger
              setShowFilters={(index?: number) => {
                setShowFilters(true)
                setInitialOpen(index)
              }}
              className={clsx('grid-in-filter text-p')}
              aggregations={filters as unknown as Aggregation[]}
            />
          )}
        </div>
        <div className="flex gap-[42px]">
          <GridToggle
            isSearch
            className={clsx('hidden grid-in-columns lg:block')}
            gridColumnsNumber={gridColumnsNumber}
            setGridColumnsNumber={setGridColumnsNumber}
          />
          {sortingOptions?.length > 0 && (
            <CioSortSelect options={sortingOptions} handleSortChange={handleSortChange} />
          )}
        </div>
      </div>

      {(isInitialLoading ||
        (isLoading && typeof router?.query?.page !== 'undefined') ||
        (hasPreviousPage && isFetchingPreviousPage)) && (
        <LoadingColumns className="px-5 lg:px-[50px]" />
      )}

      {!isInitialLoading && (
        <>
          {hasPreviousPage && !isFetchingPreviousPage && (
            <div className="grid grid-cols-[1fr_auto_1fr] p-6 lg:p-16">
              <ButtonMain
                className="col-start-2 px-10"
                variant="Secondary"
                onClick={() => loadLess()}
              >
                {t('Load Page')} {prevPageParam()}
              </ButtonMain>
            </div>
          )}

          <ul
            className={clsx(
              'grid w-full grid-cols-2 gap-y-2.5 gap-x-2.5 px-5 lg:grid-cols-6 lg:gap-y-[65px] lg:gap-x-5 lg:px-[50px]',
            )}
            style={{ gridTemplateColumns: `repeat(${gridColumnsNumber}, minmax(0, 1fr))` }}
            data-cnstrc-search={cioType === 'search' ? 'true' : undefined}
            data-cnstrc-browse={cioType === 'browse' ? 'true' : undefined}
            data-cnstrc-filter-name={cioType === 'browse' ? cioSubject.filterName : undefined}
            data-cnstrc-filter-value={cioType === 'browse' ? cioSubject.filterValue : undefined}
            data-cnstrc-num-results={
              isLoading ? undefined : data?.pages?.[0]?.response?.total_num_results || 0
            }
          >
            {data?.pages
              .flatMap((page) => page?.response?.results)
              .map((result: any, i) => (
                <li
                  key={`search_result_${i}`}
                  data-cnstrc-item-id={result?.data?.id}
                  data-cnstrc-item-name={result.value}
                  data-cnstrc-item-variation-id={result?.data?.variation_id}
                  data-cnstrc-item-price={result?.data?.price}
                  data-cnstrc-sl-campaign-id={result?.labels?.sl_campaign_id}
                >
                  <ProductCard
                    useCio
                    cioForceShowVariation={!!showVariationsStrategy || hasSizeFilter}
                    className="border-none !p-0"
                    initialInView={isDesktop ? i <= 6 : i <= 4}
                    imageLoadStrategy={getImageLoadStrategy(i)}
                    {...productMapper(result)}
                  />
                </li>
              ))}
          </ul>
        </>
      )}
      {isFetchingNextPage && hasResults && <LoadingColumns className="px-5 lg:px-[50px]" />}
      <CioFilters
        filters={filters}
        showFilters={showFilters}
        handleFilterToggle={setShowFilters}
        handleFilterChange={handleFilterChange}
        handleOpenExactFilter={handleOpenExactFilter}
        initiallyOpenedFilter={initialOpen}
      />
      {!isFetchingNextPage && hasResults && (
        <div className="grid grid-cols-[1fr_auto_1fr] p-6 lg:py-[110px]">
          {/* hasNextPage returns true when totalCount === PAGE_SIZE * currentPage hence this additional check   */}
          {hasNextPage && totalCount && totalCount > PAGE_SIZE * currentPage ? (
            <>
              <ButtonMain
                variant="Secondary"
                className="col-start-2 px-10"
                onClick={() => loadMore()}
                disabled={!hasNextPage || isFetchingNextPage}
              >
                {t('Load More')}
              </ButtonMain>
              {totalCount && (
                <p className="col-start-2 mx-auto mt-2 text-p-small text-grey-dark">
                  {PAGE_SIZE * currentPage} / {totalCount} {t('results')}
                </p>
              )}
            </>
          ) : null}
        </div>
      )}
    </>
  )
}
